import { useEffect, useCallback } from "react";
import type { ReactNode } from "react";
import { cn } from "@/lib/utils";
import { Button } from "./button";
import { X } from "lucide-react";

interface OverlayPanelProps {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  className?: string;
  /** Panel title shown in the header */
  title?: ReactNode;
  /** Optional badge/count next to the title */
  badge?: ReactNode;
  /** Title color class, e.g. "text-blue-400" */
  titleClassName?: string;
}

export function OverlayPanel({
  open,
  onClose,
  children,
  className,
  title,
  badge,
  titleClassName,
}: OverlayPanelProps) {
  const handleEscape = useCallback((e: KeyboardEvent) => {
    if (e.key === "Escape") onClose();
  }, [onClose]);

  useEffect(() => {
    if (!open) return;
    document.addEventListener("keydown", handleEscape);
    return () => { document.removeEventListener("keydown", handleEscape); };
  }, [open, handleEscape]);

  if (!open) return null;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-40" onClick={onClose} aria-hidden="true" />
      {/* Panel */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === "string" ? title : "Panel"}
        className={cn(
          "fixed top-4 right-4 bottom-4 z-50 w-[min(840px,calc(100vw-2rem))] rounded-2xl border border-white/[0.08] shadow-2xl overflow-hidden animate-in slide-in-from-right duration-200 flex flex-col",
          "bg-background/85 backdrop-blur-xl",
          className
        )}
      >
        {title && (
          <div className="px-5 py-3 border-b border-white/[0.06]">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 min-w-0">
                <span className={cn("text-sm font-semibold", titleClassName)}>{title}</span>
                {badge}
              </div>
              <Button variant="ghost" size="icon" className="h-11 w-11 md:h-7 md:w-7" onClick={onClose}>
                <X className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        )}
        {children}
      </div>
    </>
  );
}
